import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import EventBlock from "../EventBlock"
import { getJournalIcon } from "../../../utils"
import { getEventColor, getEventBorderColor } from "pagesV2/pro/Calendar/utils"
import styles from "../index.module.css"

const NonAgendaWeekView = ({ calendarDays, normalizedEventsByDay, timeOffByDay, isWeekEmpty, journalNoteDates, isCompact, active, canViewJournal, canCreateJournal, canEditJournal, getVisibleCount, showMore, showLess, MAX_VISIBLE, setIsBookingModalOpen, setSelectedBookingId, onJournalOpen }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.nonAgendaWrapper}>
      <table className={styles.nonAgendaTable}>
        <thead>
          <tr>
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD")
              const hasJournalNote = journalNoteDates?.includes(dayStr)
              const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal
              const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
              return (
                <th key={dayStr} className={styles.nonAgendaHeaderCell}>
                  <Flex gap={10}>
                    <Flex vertical align="center" justify="center" className="flex-1">
                      <Text fw="500">{dayLabel}</Text>
                      <Text fw="500">{day.format("D")}</Text>
                    </Flex>
                    {hasJournalPermission && hasJournalNote && (
                      <Flex justify="center" className="mt-xxs">
                        <IconWithBg disabled={!active} icon={getJournalIcon(journalNoteDates, dayStr)} onClick={active ? () => onJournalOpen(dayStr) : undefined} />
                      </Flex>
                    )}
                  </Flex>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {!isWeekEmpty && (
            <tr>
              {calendarDays.map((day) => {
                const dayStr = day.format("YYYY-MM-DD")
                const dayEvents = normalizedEventsByDay[dayStr] || []
                const visibleCount = getVisibleCount(dayStr)
                const visibleEvents = dayEvents.slice(0, visibleCount)
                const allVisible = visibleCount >= dayEvents.length
                const timeOffForDay = timeOffByDay[dayStr]
                return (
                  <td key={dayStr} className={styles.nonAgendaBodyCell}>
                    {timeOffForDay?.allDay && <div aria-hidden className={styles.nonAgendaAllDayOverlay} />}
                    {!timeOffForDay?.allDay && (timeOffForDay?.ranges || []).length > 0 && (
                      <>
                        <div aria-hidden className={styles.nonAgendaTimeOffOverlay} />
                        <div className={styles.nonAgendaTimeOffRangesList}>
                          {timeOffForDay.ranges.map((toff) => (
                            <div key={`${dayStr}-${toff.start.format("HH:mm")}`} className={styles.nonAgendaTimeOffRange}>
                              {toff.start.format("h:mm A")} – {toff.end.format("h:mm A")}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {visibleEvents.map((ev) => (
                      <EventBlock key={ev.id ?? ev.title + ev.start} event={ev} eventColor={getEventColor(ev.payment_status)} eventBorderColor={getEventBorderColor(ev.payment_status)} setIsBookingModalOpen={setIsBookingModalOpen} setSelectedBookingId={setSelectedBookingId} />
                    ))}
                    {!allVisible && dayEvents.length > MAX_VISIBLE && (
                      <Button className={styles.viewMoreButton} type="link" size="small" onClick={() => showMore(dayStr)}>{t("calendar.view_more")}</Button>
                    )}
                    {allVisible && dayEvents.length > MAX_VISIBLE && (
                      <Button className={styles.viewLessButton} type="link" size="small" onClick={() => showLess(dayStr)}>{t("calendar.read_less", "Read less")}</Button>
                    )}
                  </td>
                )
              })}
            </tr>
          )}
          {isWeekEmpty && (
            <tr>
              <td colSpan={calendarDays.length} className={styles.nonAgendaEmptyCell}>{t("calendar.no_records", "No records found")}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

NonAgendaWeekView.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  normalizedEventsByDay: PropTypes.object.isRequired,
  timeOffByDay: PropTypes.object.isRequired,
  isWeekEmpty: PropTypes.bool,
  journalNoteDates: PropTypes.array,
  isCompact: PropTypes.bool,
  active: PropTypes.bool,
  canViewJournal: PropTypes.bool,
  canCreateJournal: PropTypes.bool,
  canEditJournal: PropTypes.bool,
  getVisibleCount: PropTypes.func.isRequired,
  showMore: PropTypes.func.isRequired,
  showLess: PropTypes.func.isRequired,
  MAX_VISIBLE: PropTypes.number,
  setIsBookingModalOpen: PropTypes.func.isRequired,
  setSelectedBookingId: PropTypes.func.isRequired,
  onJournalOpen: PropTypes.func.isRequired,
}

export default NonAgendaWeekView
